/* Storage layer for the shared journal.
   - On the deployed site it talks to the Cloudflare Pages Function (/api/journal),
     which persists to Cloudflare D1 (strongly consistent) + R2 (media bytes).
   - In local CRA dev (react-scripts start, no function) it transparently falls
     back to localStorage so the whole UI is testable offline. The two never mix
     on a given environment. (Use `wrangler pages dev` to exercise the real API.) */

const FN = '/api/journal';
const LS_CFG = 'anusha_journal_config';
const LS_ENTRY_PREFIX = 'anusha_journal_entry_';
const LS_ENTRY = (d) => LS_ENTRY_PREFIX + d;
const LS_GALLERY = 'anusha_gallery';
const LS_PHOTO = (id) => 'anusha_photo_' + id;
const LS_EVENTS = 'anusha_events';
const LS_LETTERS = 'anusha_letterbox';
const LS_VOICES = 'anusha_voices';
const LS_VOICE = (id) => 'anusha_voice_' + id;
const LS_REASONS = 'anusha_reasons';
const LS_COUPONS = 'anusha_coupons';
const LS_NUDGES = 'anusha_nudges';

const todayYmdLocal = () => {
  const d = new Date();
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

let _mode = null;

async function sha256(s) {
  const data = new TextEncoder().encode('anusha::feral::' + s);
  const buf = await crypto.subtle.digest('SHA-256', data);
  return Array.from(new Uint8Array(buf))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

async function detectMode() {
  if (_mode) return _mode;
  try {
    const r = await fetch(`${FN}?action=status`, { headers: { accept: 'application/json' } });
    const ct = r.headers.get('content-type') || '';
    if (r.ok && ct.includes('application/json')) {
      _mode = 'backend';
      return _mode;
    }
  } catch (e) {
    /* network/function unavailable → local */
  }
  _mode = 'local';
  return _mode;
}

async function api(action, { method = 'GET', pass, body } = {}) {
  const opts = { method, headers: {} };
  if (pass != null) opts.headers['x-journal-pass'] = pass;
  if (body) {
    opts.headers['content-type'] = 'application/json';
    opts.body = JSON.stringify(body);
  }
  const r = await fetch(`${FN}?action=${action}`, opts);
  const data = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(data.error || `Request failed (${r.status})`);
  return data;
}

const lsGet = (k, d) => {
  try {
    const v = JSON.parse(localStorage.getItem(k));
    return v == null ? d : v;
  } catch {
    return d;
  }
};
const lsSet = (k, v) => localStorage.setItem(k, JSON.stringify(v));
const uid = () =>
  crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(16).slice(2)}`;

const store = {
  async mode() {
    return detectMode();
  },

  async status() {
    if ((await detectMode()) === 'backend') return api('status');
    return { initialized: !!lsGet(LS_CFG, null) };
  },

  async setup(passphrase) {
    if ((await detectMode()) === 'backend') {
      return api('setup', { method: 'POST', body: { passphrase } });
    }
    if (lsGet(LS_CFG, null)) throw new Error('Already set up.');
    lsSet(LS_CFG, { passHash: await sha256(passphrase), createdAt: new Date().toISOString() });
    return { ok: true };
  },

  async verify(passphrase) {
    if ((await detectMode()) === 'backend') return api('verify', { pass: passphrase });
    // Local dev has no function/secret — accept any non-empty passcode so the
    // gate flow is testable. Real enforcement happens server-side in production.
    return { ok: !!String(passphrase).trim() };
  },

  async dates(pass) {
    if ((await detectMode()) === 'backend') return (await api('dates', { pass })).dates || [];
    const out = [];
    for (let i = 0; i < localStorage.length; i += 1) {
      const k = localStorage.key(i);
      if (k && k.startsWith(LS_ENTRY_PREFIX)) {
        const arr = lsGet(k, []);
        if (arr && arr.length) out.push(k.slice(LS_ENTRY_PREFIX.length));
      }
    }
    return out;
  },

  async entries(pass, date) {
    if ((await detectMode()) === 'backend') {
      return (await api(`entries&date=${encodeURIComponent(date)}`, { pass })).entries || [];
    }
    return lsGet(LS_ENTRY(date), []);
  },

  async add(pass, { date, author, text, mood }) {
    if ((await detectMode()) === 'backend') {
      return (await api('add', { method: 'POST', pass, body: { date, author, text, mood } })).entry;
    }
    const arr = lsGet(LS_ENTRY(date), []);
    const entry = {
      id: uid(),
      author: author || 'Us',
      text,
      mood: mood || '',
      createdAt: new Date().toISOString(),
    };
    arr.push(entry);
    lsSet(LS_ENTRY(date), arr);
    return entry;
  },

  async remove(pass, date, id) {
    if ((await detectMode()) === 'backend') {
      return api('delete', { method: 'POST', pass, body: { date, id } });
    }
    const arr = lsGet(LS_ENTRY(date), []).filter((e) => e.id !== id);
    if (arr.length) lsSet(LS_ENTRY(date), arr);
    else localStorage.removeItem(LS_ENTRY(date));
    return { ok: true };
  },

  // ---------- photos ----------
  async photos(pass, date) {
    if ((await detectMode()) === 'backend') {
      const q = date ? `photos&date=${encodeURIComponent(date)}` : 'photos';
      return (await api(q, { pass })).photos || [];
    }
    const gallery = lsGet(LS_GALLERY, []);
    const list = (date ? gallery.filter((p) => p.date === date) : gallery).slice();
    list.sort((a, b) => (a.date === b.date ? (a.createdAt < b.createdAt ? 1 : -1) : a.date < b.date ? 1 : -1));
    return list;
  },

  async photoUrl(pass, id) {
    if ((await detectMode()) === 'backend') {
      const r = await fetch(`${FN}?action=photo&id=${encodeURIComponent(id)}`, {
        headers: { 'x-journal-pass': pass },
      });
      if (!r.ok) throw new Error('Could not load photo.');
      return URL.createObjectURL(await r.blob());
    }
    const b64 = localStorage.getItem(LS_PHOTO(id));
    return b64 ? `data:image/jpeg;base64,${b64}` : null;
  },

  async addPhoto(pass, { date, caption, author, base64 }) {
    if ((await detectMode()) === 'backend') {
      return (
        await api('addphoto', { method: 'POST', pass, body: { date, caption, author, data: base64 } })
      ).photo;
    }
    const id = uid();
    const meta = {
      id, date, caption: caption || '', author: author || 'Us', createdAt: new Date().toISOString(),
    };
    localStorage.setItem(LS_PHOTO(id), base64);
    const gallery = lsGet(LS_GALLERY, []);
    gallery.push(meta);
    lsSet(LS_GALLERY, gallery);
    return meta;
  },

  async delPhoto(pass, id) {
    if ((await detectMode()) === 'backend') {
      return api('delphoto', { method: 'POST', pass, body: { id } });
    }
    localStorage.removeItem(LS_PHOTO(id));
    lsSet(LS_GALLERY, lsGet(LS_GALLERY, []).filter((p) => p.id !== id));
    return { ok: true };
  },

  async editPhoto(pass, id, fields) {
    if ((await detectMode()) === 'backend') {
      return (await api('editphoto', { method: 'POST', pass, body: { id, ...fields } })).photo;
    }
    const gallery = lsGet(LS_GALLERY, []);
    let updated = null;
    const next = gallery.map((p) => {
      if (p.id !== id) return p;
      updated = { ...p, ...fields };
      return updated;
    });
    if (updated) lsSet(LS_GALLERY, next);
    return updated;
  },

  // ---------- events / big days ----------
  async events(pass) {
    if ((await detectMode()) === 'backend') return (await api('events', { pass })).events || [];
    return lsGet(LS_EVENTS, []);
  },

  async addEvent(pass, { title, date, emoji, repeat }) {
    if ((await detectMode()) === 'backend') {
      return (await api('addevent', { method: 'POST', pass, body: { title, date, emoji, repeat } }))
        .event;
    }
    const event = {
      id: uid(),
      title,
      date,
      emoji: emoji || '💖',
      repeat: repeat || 'yearly',
      createdAt: new Date().toISOString(),
    };
    const events = lsGet(LS_EVENTS, []);
    events.push(event);
    lsSet(LS_EVENTS, events);
    return event;
  },

  async delEvent(pass, id) {
    if ((await detectMode()) === 'backend') {
      return api('delevent', { method: 'POST', pass, body: { id } });
    }
    lsSet(LS_EVENTS, lsGet(LS_EVENTS, []).filter((ev) => ev.id !== id));
    return { ok: true };
  },

  // ---------- letterbox ----------
  async letters(pass, me) {
    if ((await detectMode()) === 'backend') {
      return (await api(`letters&me=${encodeURIComponent(me || '')}`, { pass })).letters || [];
    }
    const today = todayYmdLocal();
    return lsGet(LS_LETTERS, []).map((l) => {
      const sealed = !!(l.sealedUntil && l.sealedUntil > today);
      if (sealed && l.from !== me) return { ...l, body: null, sealed };
      return { ...l, sealed };
    });
  },

  async addLetter(pass, { from, to, title, body, sealedUntil }) {
    if ((await detectMode()) === 'backend') {
      return (
        await api('addletter', { method: 'POST', pass, body: { from, to, title, body, sealedUntil } })
      ).letter;
    }
    const letter = {
      id: uid(), from, to, title: title || '', body,
      sealedUntil: sealedUntil || null, openedAt: null, createdAt: new Date().toISOString(),
    };
    const letters = lsGet(LS_LETTERS, []);
    letters.push(letter);
    lsSet(LS_LETTERS, letters);
    return letter;
  },

  async openLetter(pass, id) {
    if ((await detectMode()) === 'backend') {
      return (await api('openletter', { method: 'POST', pass, body: { id } })).letter;
    }
    const letters = lsGet(LS_LETTERS, []);
    let opened = null;
    const next = letters.map((l) => {
      if (l.id !== id) return l;
      opened = l.openedAt ? l : { ...l, openedAt: new Date().toISOString() };
      return opened;
    });
    lsSet(LS_LETTERS, next);
    return opened;
  },

  async delLetter(pass, id) {
    if ((await detectMode()) === 'backend') {
      return api('delletter', { method: 'POST', pass, body: { id } });
    }
    lsSet(LS_LETTERS, lsGet(LS_LETTERS, []).filter((l) => l.id !== id));
    return { ok: true };
  },

  // ---------- voice notes ----------
  async voices(pass, date) {
    if ((await detectMode()) === 'backend') {
      const q = date ? `voices&date=${encodeURIComponent(date)}` : 'voices';
      return (await api(q, { pass })).voices || [];
    }
    const voices = lsGet(LS_VOICES, []);
    const list = (date ? voices.filter((v) => v.date === date) : voices).slice();
    list.sort((a, b) => (a.date === b.date ? (a.createdAt < b.createdAt ? 1 : -1) : a.date < b.date ? 1 : -1));
    return list;
  },

  async voiceUrl(pass, id) {
    if ((await detectMode()) === 'backend') {
      const r = await fetch(`${FN}?action=voice&id=${encodeURIComponent(id)}`, {
        headers: { 'x-journal-pass': pass },
      });
      if (!r.ok) throw new Error('Could not load the recording.');
      return URL.createObjectURL(await r.blob());
    }
    const v = lsGet(LS_VOICES, []).find((x) => x.id === id);
    const b64 = localStorage.getItem(LS_VOICE(id));
    return b64 ? `data:${(v && v.mime) || 'audio/webm'};base64,${b64}` : null;
  },

  async addVoice(pass, { date, author, caption, mime, dur, base64 }) {
    if ((await detectMode()) === 'backend') {
      return (
        await api('addvoice', {
          method: 'POST', pass, body: { date, author, caption, mime, dur, data: base64 },
        })
      ).voice;
    }
    const id = uid();
    const meta = {
      id, date, caption: caption || '', author: author || 'Us',
      mime: mime || 'audio/webm', dur: dur || 0, createdAt: new Date().toISOString(),
    };
    localStorage.setItem(LS_VOICE(id), base64);
    const voices = lsGet(LS_VOICES, []);
    voices.push(meta);
    lsSet(LS_VOICES, voices);
    return meta;
  },

  async delVoice(pass, id) {
    if ((await detectMode()) === 'backend') {
      return api('delvoice', { method: 'POST', pass, body: { id } });
    }
    localStorage.removeItem(LS_VOICE(id));
    lsSet(LS_VOICES, lsGet(LS_VOICES, []).filter((v) => v.id !== id));
    return { ok: true };
  },

  // ---------- reasons jar ----------
  async reasons(pass) {
    if ((await detectMode()) === 'backend') return (await api('reasons', { pass })).reasons || [];
    return lsGet(LS_REASONS, []).sort((a, b) => (a.createdAt < b.createdAt ? -1 : 1));
  },

  async addReason(pass, { text, author }) {
    if ((await detectMode()) === 'backend') {
      return (await api('addreason', { method: 'POST', pass, body: { text, author } })).reason;
    }
    const reason = { id: uid(), text, author: author || 'Us', createdAt: new Date().toISOString() };
    const reasons = lsGet(LS_REASONS, []);
    reasons.push(reason);
    lsSet(LS_REASONS, reasons);
    return reason;
  },

  async delReason(pass, id) {
    if ((await detectMode()) === 'backend') {
      return api('delreason', { method: 'POST', pass, body: { id } });
    }
    lsSet(LS_REASONS, lsGet(LS_REASONS, []).filter((r) => r.id !== id));
    return { ok: true };
  },

  // ---------- love coupons ----------
  async coupons(pass) {
    if ((await detectMode()) === 'backend') return (await api('coupons', { pass })).coupons || [];
    return lsGet(LS_COUPONS, []).sort((a, b) => (a.createdAt < b.createdAt ? -1 : 1));
  },

  async addCoupon(pass, { title, emoji, from }) {
    if ((await detectMode()) === 'backend') {
      return (await api('addcoupon', { method: 'POST', pass, body: { title, emoji, from } })).coupon;
    }
    const coupon = {
      id: uid(), title, emoji: emoji || '🎟️', from: from || 'Us',
      redeemedAt: null, redeemedBy: null, createdAt: new Date().toISOString(),
    };
    const coupons = lsGet(LS_COUPONS, []);
    coupons.push(coupon);
    lsSet(LS_COUPONS, coupons);
    return coupon;
  },

  async redeemCoupon(pass, id, by) {
    if ((await detectMode()) === 'backend') {
      return (await api('redeemcoupon', { method: 'POST', pass, body: { id, by } })).coupon;
    }
    const coupons = lsGet(LS_COUPONS, []);
    let redeemed = null;
    const next = coupons.map((c) => {
      if (c.id !== id || c.redeemedAt) return c;
      redeemed = { ...c, redeemedAt: new Date().toISOString(), redeemedBy: by || 'Us' };
      return redeemed;
    });
    if (!redeemed) throw new Error('Already redeemed. 😏');
    lsSet(LS_COUPONS, next);
    return redeemed;
  },

  async delCoupon(pass, id) {
    if ((await detectMode()) === 'backend') {
      return api('delcoupon', { method: 'POST', pass, body: { id } });
    }
    lsSet(LS_COUPONS, lsGet(LS_COUPONS, []).filter((c) => c.id !== id));
    return { ok: true };
  },

  // ---------- nudges / push ----------
  async vapidPublic(pass) {
    if ((await detectMode()) === 'backend') return (await api('vapidpublic', { pass })).publicKey || null;
    return null; // no real push without the deployed function
  },

  async subscribePush(pass, subscription, who) {
    if ((await detectMode()) === 'backend') {
      return api('subscribe', { method: 'POST', pass, body: { subscription, who } });
    }
    return { ok: true };
  },

  async unsubscribePush(pass, endpoint) {
    if ((await detectMode()) === 'backend') {
      return api('unsubscribe', { method: 'POST', pass, body: { endpoint } });
    }
    return { ok: true };
  },

  async sendNudge(pass, { from, kind, text, emoji }) {
    if ((await detectMode()) === 'backend') {
      return api('nudge', { method: 'POST', pass, body: { from, kind, text, emoji } });
    }
    const to = from === 'Tanush' ? 'Anusha' : 'Tanush';
    const nudge = {
      id: uid(), from, to, kind: kind || 'love', text: text || '', emoji: emoji || '💗',
      createdAt: new Date().toISOString(), seenAt: null,
    };
    const all = lsGet(LS_NUDGES, []);
    all.push(nudge);
    lsSet(LS_NUDGES, all);
    return { ok: true, nudge, delivered: 0, devices: 0 };
  },

  // dir='all' returns both sent + received (a two-way feed); default is received.
  async nudges(pass, me, dir) {
    if ((await detectMode()) === 'backend') {
      const q = `nudges&me=${encodeURIComponent(me || '')}${dir === 'all' ? '&dir=all' : ''}`;
      const r = await api(q, { pass });
      return { nudges: r.nudges || [], unseen: r.unseen || 0 };
    }
    const list = lsGet(LS_NUDGES, [])
      .filter((n) => (dir === 'all' ? n.to === me || n.from === me : n.to === me))
      .sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1));
    return { nudges: list, unseen: list.filter((n) => n.to === me && !n.seenAt).length };
  },

  async seenNudges(pass, me) {
    if ((await detectMode()) === 'backend') {
      return api('seen', { method: 'POST', pass, body: { me } });
    }
    lsSet(LS_NUDGES, lsGet(LS_NUDGES, []).map((n) => (n.to === me && !n.seenAt ? { ...n, seenAt: new Date().toISOString() } : n)));
    return { ok: true };
  },

  // everything at once, for the timeline
  async all(pass) {
    if ((await detectMode()) === 'backend') {
      const r = await api('all', { pass });
      return { entriesByDate: r.entriesByDate || {}, photos: r.photos || [], voices: r.voices || [] };
    }
    const entriesByDate = {};
    for (let i = 0; i < localStorage.length; i += 1) {
      const k = localStorage.key(i);
      if (k && k.startsWith(LS_ENTRY_PREFIX)) {
        const arr = lsGet(k, []);
        if (arr && arr.length) entriesByDate[k.slice(LS_ENTRY_PREFIX.length)] = arr;
      }
    }
    return { entriesByDate, photos: lsGet(LS_GALLERY, []), voices: lsGet(LS_VOICES, []) };
  },
};

export default store;
